import type { Theme } from '@mui/material';

import { goPalette, tsGoUnit } from '../tsGoThemeVars';

function getLabelCloseIconStyle(theme: Theme) {
	return {
		// fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
		color: theme.palette.tsGoColor.gray,
		cursor: 'pointer',
		alignSelf: 'flex-start',

		'&:hover': {
			color: theme.palette.tsGoColor.black,
		},
	};
}

function getMobileCloseIconStyle(theme: Theme) {
	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.big),
		stroke: theme.palette.tsGoColor.gray,
		fontWeight: theme.typography.tsFontWeight.semiBold,
		strokeWidth: 3,
	};
}

function getCloseIconStyle(theme: Theme) {
	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.big),
		fontWeight: 'bold',
		stroke: theme.palette.tsGoColor.white,
		strokeWidth: '3px',
		cursor: 'pointer',
	};
}

function getArrowRightIconActiveStyle(theme: Theme) {
	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
	};
}

function getSidebarHeaderIcon(theme: Theme, ownerState: any) {
	const defaultStyles = {
		position: 'absolute',
		backgroundSize: '15px 15px',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',
		color: theme.palette.tsGoColor.gray,
		borderRadius: tsGoUnit.tsUnitOneAndHalf,
		height: tsGoUnit.tsUnitOneAndHalf,
		width: tsGoUnit.tsUnitOneAndHalf,
	};
	if (ownerState.className === 'animateLeft') {
		return {
			...defaultStyles,
			transform: 'scaleX(-1)',
			opacity: 1,

			'&:hover': {
				animation: 'bounce 0.5s linear',
				backgroundColor: 'rgba(25, 34, 38, 1)',
			},
		};
	} else if (ownerState.className === 'animateRight') {
		return {
			...defaultStyles,
			transform: 'rotate(0deg)',
			left: theme.spacing(-1),
		};
	} else {
		return {
			...defaultStyles,
		};
	}
}

function getArrowRightIconDefaultStyle(theme: Theme) {
	return {
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
		color: theme.palette.tsGoColor.grayLight,
	};
}

function getCheckIconStyle() {
	return {
		position: 'absolute',
		right: '5px',
		top: '7px',
	};
}

function getTeamSwitcherCloseIconStyle(theme: Theme) {
	return {
		alignItems: 'center',
		borderRadius: tsGoUnit.tsUnitOneAndHalf,
		display: 'flex',
		flex: 1,
		height: tsGoUnit.tsUnitOneAndHalf,
		justifyContent: 'center',
		position: 'absolute',
		right: theme.tsGoUnit.tsUnitHalf,
		top: '15px',
		transition: 'min-width 0.3s ease-out, max-width 0.3s ease-out',
		width: tsGoUnit.tsUnitOneAndHalf,
		zIndex: 1,
		color: goPalette.tsGoColor.gray,
		cursor: 'pointer',
	};
}

function getTeamSwitcherCheckIconStyle() {
	return {
		position: 'absolute',
		right: tsGoUnit.tsUnitHalf,
		top: '15px',
		stroke: goPalette.tsGoColor.white,
	};
}

function getTeamSwitcherArrowIconStyle(theme: Theme) {
	return {
		position: 'absolute',
		right: tsGoUnit.tsUnit,
		top: '18px',
		color: theme.palette.tsGoColor.gray,
		fontSize: tsGoUnit.tsUnit,
	};
}

function getMyRequestStatusIcon(ownerState, theme: Theme) {
	const defaultStyles = {
		position: 'absolute',
		left: 0,
		top: tsGoUnit.tsUnit,
		strokeWidth: 1,
		marginLeft: '20px',
	};
	switch (ownerState.className) {
		case 'approved': {
			return {
				...defaultStyles,
				'&.approved': {
					stroke: `${theme.palette.tsGoColor.green} !important`,
					color: `${theme.palette.tsGoColor.green} !important`,
				},
			};
		}
		case 'pending': {
			return {
				...defaultStyles,
				'&.pending': {
					color: `${goPalette.tsGoColor.yellow} !important`,
				},
			};
		}
		case 'error':
		case 'declined': {
			return {
				...defaultStyles,
				'&.declined, &.error': {
					stroke: `${goPalette.tsGoColor.red} !important`,
					color: `${goPalette.tsGoColor.red} !important`,
				},
			};
		}
		default: {
			return {
				...defaultStyles,
			};
		}
	}
}

export const muiIconOverrides = {
	styleOverrides: {
		root: ({ ownerState, theme }) => ({
			variants: [
				{
					props: {
						'ts-variant': 'labelCloseIcon',
					},
					style: getLabelCloseIconStyle(theme),
				},
				{
					props: {
						'ts-variant': 'closeIcon',
					},
					style: getCloseIconStyle(theme),
				},
				{
					props: {
						className: 'arrowRightIcon',
					},
					style: getArrowRightIconDefaultStyle(theme),
				},
				{
					props: {
						className: 'arrowRightIcon active',
					},
					style: getArrowRightIconActiveStyle(theme),
				},
				{
					props: {
						'ts-variant': 'sidebarHeaderIcon',
					},
					style: getSidebarHeaderIcon(theme, ownerState),
				},
				{
					props: {
						'ts-variant': 'mobileCloseIcon',
					},
					style: getMobileCloseIconStyle(theme),
				},
				{
					props: {
						'ts-variant': 'checkIcon',
					},
					style: getCheckIconStyle(),
				},
				{
					props: {
						'ts-variant': 'teamSwitcherCloseIcon',
					},
					style: getTeamSwitcherCloseIconStyle(theme),
				},
				{
					props: {
						'ts-variant': 'teamSwitcherCheckIcon',
					},
					style: getTeamSwitcherCheckIconStyle(),
				},
				{
					props: {
						'ts-variant': 'teamSwitcherArrowIcon',
					},
					style: getTeamSwitcherArrowIconStyle(theme),
				},
				{
					props: {
						'ts-variant': 'requestStatusIcon',
					},
					style: getMyRequestStatusIcon(ownerState, theme),
				},
			],
		}),
	},
};
